// src/pages/Dashboard.js
import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "../firebase";
import { db } from "../firebase";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState({
    drivers: 0,
    trucks: 0,
    trips: 0,
    complaints: 0,
    activeTrips: 0,
    availableDrivers: 0,
    activeTrucks: 0,
    serviceDue: 0,
    complianceAlerts: 0,
    monthlyProfit: 0,
    profitChange: 0,
  });
  const [profitTrend, setProfitTrend] = useState([]);
  const [truckUtilization, setTruckUtilization] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [recentTrips, setRecentTrips] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [
          driversSnap,
          trucksSnap,
          tripsSnap,
          compSnap,
          financesSnap,
          docsSnap,
        ] = await Promise.all([
          getDocs(collection(db, "drivers")),
          getDocs(collection(db, "trucks")),
          getDocs(collection(db, "trips")),
          getDocs(collection(db, "complaints")),
          getDocs(collection(db, "trip_finances")),
          getDocs(collection(db, "documents")),
        ]);

        const drivers = driversSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const trucks = trucksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const trips = tripsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const finances = financesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const documents = docsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Calculate metrics
        const activeTrips = trips.filter(t => t.status === "Active").length;
        const activeTrucks = trucks.filter(t => t.status === "Active" && !t.disposed).length;
        
        // Drivers not on active trip
        const driversOnActiveTrip = new Set(
          trips.filter(t => t.status === "Active").map(t => t.driver)
        );
        const availableDrivers = drivers.filter(d => !driversOnActiveTrip.has(d.full_name)).length;
        
        // Service due (remaining km ≤ 500)
        const serviceDue = trucks.filter(t => {
          const remaining = (t.service_interval_km || 0) - (t.current_odometer - (t.odometer_at_service || 0));
          return remaining <= 500 && remaining > 0 && t.status === "Active";
        }).length;
        
        // Compliance alerts (documents expiring within 14 days)
        const today = new Date();
        const in14Days = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
        const expiringDocsList = documents.filter(doc => {
          if (!doc.expiry_date || doc.owner_type !== "truck") return false;
          const expDate = new Date(doc.expiry_date);
          return expDate >= today && expDate <= in14Days;
        });
        
        // Monthly profit
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const currentMonthFinances = finances.filter(f => {
          const date = f.createdAt?.toDate ? f.createdAt.toDate() : new Date(f.createdAt);
          return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
        });
        const lastMonthFinances = finances.filter(f => {
          const date = f.createdAt?.toDate ? f.createdAt.toDate() : new Date(f.createdAt);
          return date.getMonth() === currentMonth - 1 && date.getFullYear() === currentYear;
        });
        
        const currentProfit = currentMonthFinances.reduce((sum, f) => sum + (f.profit || 0), 0);
        const lastProfit = lastMonthFinances.reduce((sum, f) => sum + (f.profit || 0), 0);
        const profitChange = lastProfit > 0 ? ((currentProfit - lastProfit) / lastProfit) * 100 : 0;
        
        // Profit trend (last 6 months)
        const trendData = [];
        for (let i = 5; i >= 0; i--) {
          const monthDate = new Date();
          monthDate.setMonth(monthDate.getMonth() - i);
          const monthFinances = finances.filter(f => {
            const date = f.createdAt?.toDate ? f.createdAt.toDate() : new Date(f.createdAt);
            return date.getMonth() === monthDate.getMonth() && date.getFullYear() === monthDate.getFullYear();
          });
          trendData.push({
            month: monthDate.toLocaleString('default', { month: 'short' }),
            income: monthFinances.reduce((sum, f) => sum + (f.incomeDefault || 0), 0),
            cost: monthFinances.reduce((sum, f) => sum + (f.totalCostDefault || 0), 0),
            profit: monthFinances.reduce((sum, f) => sum + (f.profit || 0), 0),
          });
        }
        
        // Truck utilization
        const utilizationData = [
          { name: "Active", value: activeTrucks, color: "#15803d" },
          { name: "Inactive", value: trucks.filter(t => t.status === "Inactive" && !t.disposed).length, color: "#64748b" },
          { name: "Maintenance", value: trucks.filter(t => t.status === "Maintenance" && !t.disposed).length, color: "#d97706" },
        ];
        
        // Recent trips (last 7 days)
        const recentTripsList = [...trips]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);
        
        // Recent activity feed
        const activity = [];
        
        // Add trip activities
        trips.slice(0, 3).forEach(trip => {
          if (trip.status === "Active") {
            activity.push({
              id: `trip-${trip.id}`,
              type: "trip",
              message: `Trip ${trip.trip_code} started by ${trip.driver || "Unknown"}`,
              time: trip.createdAt,
              icon: "✈️",
              color: "#2563eb",
              link: "/trips",
            });
          }
        });
        
        // Add expiring docs
        expiringDocsList.slice(0, 2).forEach(doc => {
          const daysLeft = Math.ceil((new Date(doc.expiry_date) - today) / (1000 * 60 * 60 * 24));
          activity.push({
            id: `doc-${doc.id}`,
            type: "compliance",
            message: `Document "${doc.doc_type}" for ${doc.owner_id} expires in ${daysLeft} days`,
            time: doc.expiry_date,
            icon: "📄",
            color: "#d97706",
            link: "/documents",
          });
        });
        
        // Add complaints
        const complaintsList = compSnap.docs.slice(0, 2).map(d => ({ id: d.id, ...d.data() }));
        complaintsList.forEach(complaint => {
          activity.push({
            id: `comp-${complaint.id}`,
            type: "complaint",
            message: `Complaint logged: ${complaint.description?.substring(0, 60)}...`,
            time: complaint.createdAt,
            icon: "⚠️",
            color: "#dc2626",
            link: "/complaints",
          });
        });
        
        // Sort activity by time (most recent first)
        activity.sort((a, b) => new Date(b.time) - new Date(a.time));
        
        setStats({
          drivers: drivers.length,
          trucks: trucks.filter(t => !t.disposed).length,
          trips: trips.length,
          complaints: compSnap.size,
          activeTrips,
          availableDrivers,
          activeTrucks,
          serviceDue,
          complianceAlerts: expiringDocsList.length,
          monthlyProfit: currentProfit,
          profitChange,
        });
        
        setProfitTrend(trendData);
        setTruckUtilization(utilizationData);
        setExpiringDocs(expiringDocsList.slice(0, 5));
        setRecentTrips(recentTripsList);
        setRecentActivity(activity.slice(0, 5));
        
        console.log("Dashboard data loaded");
      } catch (error) {
        console.error("Error loading dashboard stats:", error);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const COLORS = ["#15803d", "#64748b", "#d97706"];

  if (loading) return <LoadingSpinner />;
  if (error) return <div style={{ textAlign: "center", padding: "20px", color: "#b91c1c" }}>{error}</div>;

  return (
    <div className="dashboard">
      <h2>Dashboard</h2>
      
      {/* Row 1: Key Metrics */}
      <div className="summary-cards">
        <div className="card" style={{ borderTop: "4px solid #2563eb" }}>
          <div className="card-title">🚛 Active Trucks</div>
          <div className="card-value">{stats.activeTrucks}</div>
          <div className="card-sub">of {stats.trucks} total</div>
        </div>
        <div className="card" style={{ borderTop: "4px solid #15803d" }}>
          <div className="card-title">👨‍✈️ Available Drivers</div>
          <div className="card-value">{stats.availableDrivers}</div>
          <div className="card-sub">of {stats.drivers} total</div>
        </div>
        <div className="card" style={{ borderTop: "4px solid #d97706" }}>
          <div className="card-title">✈️ Active Trips</div>
          <div className="card-value">{stats.activeTrips}</div>
          <div className="card-sub">in progress</div>
        </div>
        <div className="card" style={{ borderTop: "4px solid #dc2626" }}>
          <div className="card-title">⚠️ Compliance Alerts</div>
          <div className="card-value" style={{ color: stats.complianceAlerts > 0 ? "#dc2626" : "#15803d" }}>
            {stats.complianceAlerts}
          </div>
          <div className="card-sub">documents expiring soon</div>
        </div>
        <div className="card" style={{ borderTop: "4px solid #7c3aed" }}>
          <div className="card-title">🔧 Service Due</div>
          <div className="card-value" style={{ color: stats.serviceDue > 0 ? "#d97706" : "#15803d" }}>
            {stats.serviceDue}
          </div>
          <div className="card-sub">trucks need service</div>
        </div>
        <div className="card" style={{ borderTop: "4px solid #0d9488" }}>
          <div className="card-title">💰 Monthly Profit</div>
          <div className="card-value">
            ${(stats.monthlyProfit || 0).toLocaleString()}
          </div>
          <div className="card-sub" style={{ color: stats.profitChange >= 0 ? "#15803d" : "#dc2626" }}>
            {stats.profitChange >= 0 ? "↑" : "↓"} {Math.abs(stats.profitChange).toFixed(1)}% vs last month
          </div>
        </div>
      </div>

      {/* Row 2: Charts */}
      <div className="dashboard-charts">
        <div className="chart-card">
          <h3>📈 Profit/Loss Trend (6 Months)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={profitTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="income" stroke="#2563eb" name="Income" strokeWidth={2} />
              <Line type="monotone" dataKey="cost" stroke="#dc2626" name="Cost" strokeWidth={2} />
              <Line type="monotone" dataKey="profit" stroke="#15803d" name="Profit" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card">
          <h3>🚛 Truck Utilization</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={truckUtilization}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {truckUtilization.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 3: Compliance & Recent Trips */}
      <div className="dashboard-two-col">
        <div className="info-card">
          <h3>⚠️ Upcoming Compliance Expiry</h3>
          {expiringDocs.length === 0 ? (
            <p className="empty-state">✅ All compliance documents up to date</p>
          ) : (
            <div className="compliance-list">
              {expiringDocs.map(doc => {
                const daysLeft = Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div key={doc.id} className="compliance-item">
                    <div className="compliance-icon">📄</div>
                    <div className="compliance-details">
                      <div className="compliance-title">{doc.doc_type || "Document"}</div>
                      <div className="compliance-owner">{doc.owner_id}</div>
                    </div>
                    <div className={`compliance-days ${daysLeft <= 7 ? "critical" : "warning"}`}>
                      {daysLeft} days left
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="info-card">
          <h3>✈️ Recent Trips</h3>
          {recentTrips.length === 0 ? (
            <p className="empty-state">No recent trips</p>
          ) : (
            <div className="recent-trips-list">
              {recentTrips.map(trip => (
                <div key={trip.id} className="recent-trip-item">
                  <div className="trip-status-badge" data-status={trip.status}>
                    {trip.status === "Active" ? "🟢" : trip.status === "Completed" ? "✅" : "📝"}
                  </div>
                  <div className="trip-details">
                    <div className="trip-code">{trip.trip_code}</div>
                    <div className="trip-info">{trip.truck} • {trip.driver}</div>
                  </div>
                  <div className="trip-date">
                    {trip.createdAt?.slice(0, 10) || "-"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 4: Recent Activity */}
      <div className="activity-card">
        <h3>📋 Recent Activity</h3>
        {recentActivity.length === 0 ? (
          <p className="empty-state">No recent activity</p>
        ) : (
          <div className="activity-feed">
            {recentActivity.map(activity => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon" style={{ backgroundColor: activity.color + "20", color: activity.color }}>
                  {activity.icon}
                </div>
                <div className="activity-content">
                  <p>{activity.message}</p>
                  <span className="activity-time">
                    {new Date(activity.time).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}